import {
  useEffect,
  useMemo,
  useRef,
  type FunctionComponent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { FormattedMessage, useIntl, type MessageDescriptor } from 'react-intl';
import Link from '@atlaskit/link';
import { useFeatureGate } from '@atlassian/feature-gates-react';

import MultiSelect from '../MultiSelect/MultiSelect';
import FieldLabel from '../MultiSelect/FieldLabel';
import {
  getAvatarOptionChipColor,
  getAvatarOptionIcon,
} from '../MultiSelect/avatarOption';
import {
  useInfo,
  useSelectedWorkspace,
} from '../../../Flow/ConsentContextProvider';
import { OAuthConsentFeatureGate } from '../../../FeatureGates/FeatureGates';
import {
  deriveSiteNameFromUrl,
  getAvailableConsentSites,
  type ConsentSite,
} from '../domain/availableSites';
import { grantedNoPermissions } from '../domain/policyState';
import { getOrgOptionState, resolveSiteOrgId } from '../domain/orgMatch';
import { useMcpContext } from '../McpContextProvider';
import { mcpSiteSelectorMessages } from './messages';
import { orgMismatchMessages } from '../orgMismatchMessages';
import {
  MCP_PERMISSIONS_MANAGEMENT_DOC_URL,
  MCP_ROVO_DOC_URL,
} from '../mcpUrls';

const MCP_SITE_SELECT_INPUT_ID = 'oauth-consent-mcp-site-select';

/**
 * A documentation link to drop into a message's `{learnMoreLink}` slot. Composed
 * rather than handed to an ADS inline message so the link stays clickable — see
 * the `message` prop on MultiSelect.
 */
const docLink = (href: string, label: MessageDescriptor): ReactElement => (
  <Link href={href} target="_blank" rel="noopener noreferrer">
    <FormattedMessage {...label} />
  </Link>
);

/** A selectable site with its display name resolved once per site. */
type NamedConsentSite = ConsentSite & { name: string };

// Pure functions of the item, so they live at module scope rather than being
// rebuilt in the component body.
const toValue = (site: NamedConsentSite): string => site.url;
const getLabel = (site: NamedConsentSite): string => site.name;
// The URL is only worth a second line when it differs from the derived name.
const getDescription = (site: NamedConsentSite): string | null =>
  site.name === site.url ? null : site.url;
// The chip is a single line, so it carries both parts: "Pug | pug.jira-dev.com".
const getChipLabel = (site: NamedConsentSite): string =>
  site.name === site.url ? site.url : `${site.name} | ${site.url}`;

/**
 * Atlassian site picker for the MCP consent screen.
 *
 * Single-select: one site is granted per consent, submitted as the
 * `selectedWorkspace` param on Accept (see McpFlowParameters) and policy-checked
 * against `/consent/workspace`. Shares the `MultiSelect` component with the
 * Bitbucket workspace selector, so the two fields read as one — avatar tile,
 * name over URL, and the same removable chip for the selection.
 *
 * A successful check that grants nothing is reported in this field's own
 * message slot, reading only `policies.site` so a Bitbucket result never
 * surfaces here. A failed request is shown full-panel by McpPolicyAlerts.
 */
const SiteSelector: FunctionComponent = (): ReactElement => {
  const { formatMessage } = useIntl();
  const info = useInfo();
  const { selectedWorkspace, setSelectedWorkspace } = useSelectedWorkspace();
  const { policies, hasOrgMismatch, bitbucketOrgId, siteOrgIndex } =
    useMcpContext();
  const shouldSortWorkspaces = useFeatureGate(
    OAuthConsentFeatureGate.MCP_SORT_WORKSPACES,
  );
  const isSiteBypassEnabled = useFeatureGate(
    OAuthConsentFeatureGate.SITE_SELECTOR_BYPASS,
  );

  const sites = useMemo(() => {
    const namedSites = getAvailableConsentSites(info).map((site) => ({
      ...site,
      // The backend resolves a name only where the URL cannot be read as one
      // (a loom workspace URL is a cloud id), so deriving is the fallback, not
      // the rule — showing "Stage" for stage.loom.com/workspace/cloud/<id>
      // names the wrong thing.
      name: site.name ?? deriveSiteNameFromUrl(site.url),
    }));
    return shouldSortWorkspaces
      ? namedSites.sort((a, b) => a.url.localeCompare(b.url))
      : namedSites;
  }, [info.availableSites, info.availableWorkspaces, shouldSortWorkspaces]);

  const hasSites = sites.length > 0;
  const availableSiteUrls = useMemo(
    () => new Set(sites.map((site) => site.url)),
    [sites],
  );
  const prefilledSiteUrlRef = useRef<string | null>(null);

  const onlySiteUrl = sites.length === 1 ? sites[0].url : null;
  useEffect(() => {
    if (selectedWorkspace) {
      // A selection the response no longer offers cannot be granted, so it is
      // dropped rather than left to fail its policy check.
      //
      // SiteBypassBanner is the exception, and the reason this reads the bypass
      // gate. It renders directly above this field (see MCP.tsx) and writes a
      // hand-typed host straight into `selectedWorkspace` — a host that is
      // absent from the offered list by definition, since the banner exists for
      // the case where /consent/info returned no sites at all. Sweeping it away
      // leaves an internal user with a "Use this site" button that visibly does
      // nothing, so while the escape hatch is switched on the offered list is
      // not treated as authoritative. That gate is internal-only, so every
      // production consent still gets the sweep — and the banner is in any case
      // the only writer here that can produce an off-list selection, as the
      // field itself only ever stores a site it offered.
      if (!availableSiteUrls.has(selectedWorkspace) && !isSiteBypassEnabled) {
        setSelectedWorkspace?.(null);
      } else if (selectedWorkspace === onlySiteUrl) {
        prefilledSiteUrlRef.current = onlySiteUrl;
      }
      return;
    }

    // Prefill a sole option once, but respect the user clearing its chip.
    if (onlySiteUrl && prefilledSiteUrlRef.current !== onlySiteUrl) {
      prefilledSiteUrlRef.current = onlySiteUrl;
      setSelectedWorkspace?.(onlySiteUrl);
    }
  }, [
    availableSiteUrls,
    isSiteBypassEnabled,
    onlySiteUrl,
    selectedWorkspace,
    setSelectedWorkspace,
  ]);

  // A selected Bitbucket workspace's org disables other-org sites, so they stay
  // listed but cannot be picked. The message branches below take priority over
  // the helper, so they gate it.
  const { isItemDisabled, helpKind } = getOrgOptionState({
    items: sites,
    otherOrgId: bitbucketOrgId,
    getOrgId: (site: NamedConsentSite) =>
      resolveSiteOrgId(siteOrgIndex, site.url),
    canShowHelp:
      !selectedWorkspace &&
      hasSites &&
      !hasOrgMismatch &&
      !grantedNoPermissions(policies.site),
  });

  const message: ReactNode = !hasSites ? (
    <FormattedMessage
      {...mcpSiteSelectorMessages.noSites}
      values={{
        learnMoreLink: docLink(
          MCP_ROVO_DOC_URL,
          mcpSiteSelectorMessages.noWorkspacesLearnMore,
        ),
      }}
    />
  ) : hasOrgMismatch ? (
    // Ahead of the policy result: the two selections cannot be granted together
    // whatever the check returned, and changing one of them is the only way
    // forward — so that is what the field should say.
    <FormattedMessage {...orgMismatchMessages.orgMismatch} />
  ) : grantedNoPermissions(policies.site) ? (
    // The check succeeded but granted nothing, which in practice means an admin
    // policy blocked it — so point at the guide for managing those policies
    // rather than suggesting another site.
    <FormattedMessage
      {...mcpSiteSelectorMessages.noPermissions}
      values={{
        learnMoreLink: docLink(
          MCP_PERMISSIONS_MANAGEMENT_DOC_URL,
          mcpSiteSelectorMessages.noPermissionsLearnMore,
        ),
      }}
    />
  ) : helpKind === 'empty' ? (
    <FormattedMessage {...mcpSiteSelectorMessages.noOrgSites} />
  ) : helpKind === 'constraint' ? (
    <FormattedMessage {...mcpSiteSelectorMessages.orgConstraint} />
  ) : undefined;

  return (
    <MultiSelect<NamedConsentSite, string>
      inputId={MCP_SITE_SELECT_INPUT_ID}
      items={sites}
      value={selectedWorkspace ? [selectedWorkspace] : []}
      toValue={toValue}
      getLabel={getLabel}
      getDescription={getDescription}
      getIcon={getAvatarOptionIcon}
      getChipColor={getAvatarOptionChipColor}
      getChipLabel={getChipLabel}
      isItemDisabled={isItemDisabled}
      isDisabled={!hasSites}
      message={message}
      messageTone={helpKind === 'none' ? 'error' : 'information'}
      placeholder={formatMessage(mcpSiteSelectorMessages.sitePlaceholder)}
      renderLabel={() => (
        <FieldLabel>
          {formatMessage(mcpSiteSelectorMessages.siteLabel)}
        </FieldLabel>
      )}
      onChange={(urls) => setSelectedWorkspace?.(urls[0] ?? null)}
    />
  );
};

export default SiteSelector;
